import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslateService} from '../../../../services/translate/translate.service';
import {FormDataService} from '../../../form/services/form-data.service';
import {GlobalModel} from '../../../../services/state/global.model';
import {LumiSelectOption} from '../../../commonUI/select/lumi-select/lumi-select.interface';
import {finalize, take} from 'rxjs/operators';
import {BasicHttpGetResult} from '../../../../services/http-service-2.0/http.interface';
import {ListItem, ListItemMenuItemEvent} from '../../../commonUI/list/complex-list-item/complex-list-item.interface';
import {MapServerService} from '../map-server.service';
import {MapServerLayer} from '../map-server.interface';
import {FormMapServerValueInterface} from '../../../form/components/form-map-server/form-map-server.interface';
import {StorageService} from '../../../../services/storage/storage.service';
import {LoggerService} from "../../../../services/logger/logger.service";
import {ServerType} from '../../../../utils/map-wmslayer-generator';

interface MapServerSettingsSavePayload {
    layers: MapServerLayer[];
    outputFormat: string;
    expectedCRS: string;
}

interface SavedMapServerLayerSettings {
    layerIds?: (number | string)[];
    outputFormat?: string;
    expectedCRS?: string;
}

@Component({
  selector: 'map-server-settings',
  templateUrl: './map-server-settings.component.html',
})
export class MapServerSettingsComponent implements OnInit {
    @Input() serverSettings!: FormMapServerValueInterface;
    @Output() onDelete: EventEmitter<any> = new EventEmitter();
    @Output() onEdit: EventEmitter<any> = new EventEmitter();
    @Output() onSave: EventEmitter<MapServerSettingsSavePayload> = new EventEmitter();

    public mapLayers:LumiSelectOption[] = [];
    public selectedMapLayers:LumiSelectOption[] = [];

    public outputFormatOptions: LumiSelectOption[] = [
        { id: 'geojson', name: this.ts.translate('mapserver.wfs.outputformat.geojson.recommended') },
        { id: 'application/geo+json', name: this.ts.translate('mapserver.wfs.outputformat.application_geo_plus_json') },
        { id: 'application/json', name: this.ts.translate('mapserver.wfs.outputformat.application_json') },
    ];
    public selectedOutputFormat: LumiSelectOption[] = [];

    public expectedCRSOptions: LumiSelectOption[] = [
        { id: 'AUTO', name: this.ts.translate('mapserver.wfs.expectedcrs.auto_detect') },
        { id: 'EPSG:4326', name: this.ts.translate('mapserver.wfs.expectedcrs.epsg4326') },
        { id: 'EPSG:28992', name: this.ts.translate('mapserver.wfs.expectedcrs.epsg28992') },
        { id: 'EPSG:3857', name: this.ts.translate('mapserver.wfs.expectedcrs.epsg3857') },
        { id: 'CRS:84', name: this.ts.translate('mapserver.wfs.expectedcrs.crs84') }
    ];
    public selectedExpectedCRS: LumiSelectOption[] = [];

    public isLoading:boolean = false;
    public isLoadingError:boolean = false;
    public listItem:ListItem | null = null;
    public errorMessage:string = '';

    public outputFormat: string = 'geojson';
    public expectedCRS: string = 'AUTO';

    public isWfsServer(): boolean {
        return !!this.serverSettings?.serverType?.includes(ServerType.WFS);
    }

    public getSaveTitle(): string {
        return this.isWfsServer()
            ? this.ts.translate('mapserver.wfs.savetitle')
            : this.ts.translate('project-activity.save.title');
    }

    public canEditServerSettings(): boolean {
        return !this.isLoading && !this.isLoadingError && !this.serverSettings?.disabled;
    }

    public constructor(public ts: TranslateService, public formService:FormDataService, protected model:GlobalModel, private mapServerService:MapServerService, private storage:StorageService, protected logger:LoggerService) {}

    ngOnInit(): void {
        if(this.serverSettings?.url && this.serverSettings.id && this.serverSettings.name){
            if (this.serverSettings.outputFormat) {
                this.outputFormat = this.serverSettings.outputFormat;
            }
            if (this.serverSettings.expectedCRS) {
                this.expectedCRS = this.serverSettings.expectedCRS;
            }

            this.setMapLayers(this.serverSettings)
            this.listItem = {
                id:this.serverSettings.id,
                title:this.serverSettings.name,
                icon: 'layers',
                menuItems:[
                    {
                        label: this.ts.translate('mapserver.edittitle'),
                        icon: 'edit',
                        action: 'edit'
                    },
                    {
                        label: this.ts.translate('mapserver.deletetitle'),
                        icon: 'delete',
                        action: 'delete'
                    }
                ]
            }

            this.updateSelectedOutputFormatOption();
            this.updateSelectedExpectedCRSOption();
        }
    }

    public handleMenuItemClick(e:ListItemMenuItemEvent): void {
        if (e.action === 'edit') {
            this.handleClickEdit()
        } else if (e.action === 'delete') {
            this.handleClickDelete()
        }
    }

    public handleOptionSelect(e:LumiSelectOption[]): void {
        this.selectedMapLayers = e;
    }

    public handleOutputFormatSelect(e:LumiSelectOption[]): void {
        if (e && e.length > 0) {
            this.outputFormat = String(e[0].id);
        }
    }

    public handleExpectedCRSSelect(e:LumiSelectOption[]): void {
        if (e && e.length > 0) {
            this.expectedCRS = String(e[0].id);
        }
    }

    public setMapLayers(server:FormMapServerValueInterface): void {
        this.isLoading = true
        this.mapServerService.getMapServerLayers(server).pipe(
            take(1)
        ).subscribe(layers => {
            this.setSelectedMapLayers(this.serverSettings)
            this.mapLayers = layers;
        }, (error) => {
            if (error.url) {
                if (this.serverSettings.url?.substr(0, error.url.indexOf('?'))  === error.url?.substr(0, error.url.indexOf('?'))) {
                    this.storage.setValue(`${StorageService.KEY_MAP_SINGLE_WMS_ENABLED}${server.id}`, false);
                    this.isLoading = false;
                    this.isLoadingError = true;
                    this.errorMessage = this.ts.translate('mapserver.errordefault');
                    // Check if already disabled, otherwise disable the server connection
                    if(!this.serverSettings.disabled) {
                        let deleteUrl = 'settings/area/disable-server/' + server.id;
                        this.formService.disableMapServer(deleteUrl).pipe(
                            take(1),
                        ).subscribe((response) => {
                            if(this.model.currentAreaal.getValue().wmsData !== null){
                                const index = this.model.currentAreaal.getValue().wmsData.findIndex(wmsData => wmsData.id === server.id)
                                this.model.currentAreaal.getValue().wmsData[index].disabled = response as boolean;
                            }
                        }, (error) => {
                            this.logger.log('[MapServerSettingsComponent] error disabling map server: ', error);
                        })
                    }
                }
            } else {
                this.logger.log('[MapServerSettingsComponent] error without URL available ', error);
            }
        });
    }

    public setSelectedMapLayers(server:FormMapServerValueInterface): void {
        this.formService.getSelectedMapServerLayers('settings/area/get-server-layers/' + server.id).pipe(
            take(1),
            finalize(() => {this.isLoading = false; this.isLoadingError = false})
        ).subscribe(
            (selectedLayers:BasicHttpGetResult) => {
                let savedMapLayers: (number | string)[] = [];

                if (Array.isArray(selectedLayers)) {
                    savedMapLayers = selectedLayers as (number | string)[];
                } else {
                    const savedSettings = selectedLayers as SavedMapServerLayerSettings;
                    savedMapLayers = Array.isArray(savedSettings?.layerIds) ? savedSettings.layerIds : [];

                    if (savedSettings?.outputFormat) {
                        this.outputFormat = savedSettings.outputFormat;
                    }

                    if (savedSettings?.expectedCRS) {
                        this.expectedCRS = savedSettings.expectedCRS;
                    }

                    this.updateSelectedOutputFormatOption();
                    this.updateSelectedExpectedCRSOption();
                }

                this.getSelectedOptions(savedMapLayers)
            },
            (error) => {
                this.logger.log('[MapServerSettingsComponent] ', error);
            }
        )
    }

    public getSelectedOptions(savedMapLayers:(number | string)[]): void {
        let selectedMapLayers: LumiSelectOption[] = [];
        let mapLayersCopy = [...this.mapLayers];
        if(savedMapLayers !== null) {
            savedMapLayers.forEach((savedLayer) => {
                if (!mapLayersCopy.some(_layer => _layer.id === savedLayer)) {
                    selectedMapLayers.push({id:savedLayer,name:`${savedLayer} (${this.ts.translate('unavailable')})`});
                    this.mapLayers.push({id:savedLayer,name:`${savedLayer} (${this.ts.translate('unavailable')})`});
                } else {
                    const matchingLayer = mapLayersCopy.find(_layer => _layer.id === savedLayer);
                    if (matchingLayer) {
                        selectedMapLayers.push(matchingLayer);
                    }
                    let index:number = mapLayersCopy.findIndex(_layer => _layer.id === savedLayer);
                    mapLayersCopy.splice(index,1);
                }
            })
        }
        this.selectedMapLayers = selectedMapLayers;
    }

    handleClickSave(): void {
        if (!this.canEditServerSettings()) {
            return;
        }

        const layers = this.selectedMapLayers.map(option => <MapServerLayer> option)
        this.onSave.emit({
            layers: layers,
            outputFormat: this.outputFormat,
            expectedCRS: this.expectedCRS,
        })
    }

    private updateSelectedOutputFormatOption(): void {
        const selectedFormatOption = this.outputFormatOptions.find(opt => opt.id === this.outputFormat)
            || this.outputFormatOptions[0];

        this.outputFormat = String(selectedFormatOption.id);
        this.selectedOutputFormat = [selectedFormatOption];
    }

    private updateSelectedExpectedCRSOption(): void {
        const selectedCrsOption = this.expectedCRSOptions.find(opt => opt.id === this.expectedCRS)
            || this.expectedCRSOptions[0];

        this.expectedCRS = String(selectedCrsOption.id);
        this.selectedExpectedCRS = [selectedCrsOption];
    }

    handleClickEdit(): void {
        this.onEdit.emit()
    }

    handleClickDelete(): void {
        this.onDelete.emit()
    }
}
